/* eslint-disable react-hooks/exhaustive-deps */
/**
 * NotificationBell — header bell for todo follow-up reminders.
 * Polls /api/my-portal/notifications every 60s.
 */
import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/my-portal/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try {
      await api.post("/my-portal/notifications/mark-read", {});
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch { /* silent */ }
  };

  const onItemClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post("/my-portal/notifications/mark-read", { ids: [n.id] }); } catch { /* silent */ }
    }
    if (n.type === "todo_followup") navigate("/my-portal/todos");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => { if (!v) load(); return !v; })}
        data-testid="notification-bell"
        className="relative h-9 w-9 rounded-full ring-1 ring-red-100 hover:bg-red-50 flex items-center justify-center transition"
        title="Notifications"
      >
        <Bell className="h-4 w-4 text-slate-600" />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-panel"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl ring-1 ring-slate-200 shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <div className="text-xs font-bold text-slate-900">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll}
                      data-testid="notification-mark-all"
                      className="text-[11px] text-red-600 hover:underline">
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">No notifications</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={`block w-full text-left px-3 py-2 hover:bg-slate-50 transition border-b border-slate-50 ${
                  n.read ? "" : "bg-red-50/40"
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-red-600 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900">{n.title}</div>
                    {n.body && <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-1">{formatWhen(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
